/**
 * Employee Profile — full compliance dashboard for one employee.
 *
 * Tabs: Overview · Documents · Credentials · Onboarding · Activity
 * Left rail: photo + core identity + status badge
 * Actions: photo upload, status change, delete, edit fields
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2, ArrowLeft, Camera, Trash2, Edit3, Save, X, Plus, Calendar,
  Mail, Phone, MapPin, Building2, User, ShieldCheck, ClipboardList, FileText,
  Activity, Check, AlertTriangle, CalendarClock, Clock3, Upload as UploadIcon,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, differenceInCalendarDays } from "date-fns";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

import {
  getEmployee, updateEmployee, deleteEmployee, uploadEmployeePhoto,
  toggleOnboardingItem, addOnboardingItem, removeOnboardingItem,
  getEmployeeTimeline,
  EMPLOYEE_COMPLIANCE_STATUSES, EMPLOYEE_CREDENTIAL_CATEGORIES,
} from "@/lib/api";
import EmployeePhoto from "@/components/employees/EmployeePhoto";

const STATUS_META = Object.fromEntries(EMPLOYEE_COMPLIANCE_STATUSES.map((s) => [s.value, s]));
const CATEGORY_LABEL = Object.fromEntries(EMPLOYEE_CREDENTIAL_CATEGORIES.map((c) => [c.value, c.label]));

// ---------------------------------------------------------------------------
export default function EmployeeProfile({ employeeId, onBack, onSessionExpired }) {
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [tab, setTab] = useState("overview");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const emp = await getEmployee(employeeId);
      setEmployee(emp);
    } catch (err) {
      if (err?.response?.status === 401) { onSessionExpired?.(); return; }
      toast.error("Could not load employee.");
      onBack?.();
    } finally { setLoading(false); }
  }, [employeeId, onSessionExpired, onBack]);

  useEffect(() => { load(); }, [load]);

  if (loading || !employee) {
    return (
      <div className="grid place-items-center py-24">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: "#0B2545" }} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid={`employee-profile-${employee.id}`}>
      <Button variant="ghost" onClick={onBack} className="h-9 -ml-2"
              style={{ color: "#0B2545" }} data-testid="employee-back">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to directory
      </Button>

      <ProfileHeader employee={employee} onChanged={load} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent border-b w-full justify-start rounded-none h-auto p-0"
                  style={{ borderColor: "#E2E8F0" }}>
          {[
            { v: "overview",    label: "Overview",    icon: User },
            { v: "credentials", label: "Credentials", icon: ShieldCheck },
            { v: "onboarding",  label: "Onboarding",  icon: ClipboardList },
            { v: "activity",    label: "Activity",    icon: Activity },
          ].map((t) => (
            <TabsTrigger key={t.v} value={t.v}
                         data-testid={`tab-${t.v}`}
                         className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0B2545] data-[state=active]:bg-transparent px-4 py-2.5">
              <t.icon className="h-3.5 w-3.5 mr-1.5" /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab employee={employee} onChanged={load} />
        </TabsContent>
        <TabsContent value="credentials" className="mt-6">
          <CredentialsTab employee={employee} />
        </TabsContent>
        <TabsContent value="onboarding" className="mt-6">
          <OnboardingTab employee={employee} onChanged={load} />
        </TabsContent>
        <TabsContent value="activity" className="mt-6">
          <ActivityTab employee={employee} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header — photo, identity, status badge, quick actions
// ---------------------------------------------------------------------------
const ProfileHeader = ({ employee, onChanged }) => {
  const status = STATUS_META[employee.compliance_status] || STATUS_META.new_hire;
  const photoRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [photoKey, setPhotoKey] = useState(0);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Photo exceeds 5 MB."); return; }
    setUploading(true);
    try {
      await uploadEmployeePhoto(employee.id, file);
      toast.success("Photo updated.");
      setPhotoKey((k) => k + 1);   // force EmployeePhoto to refetch
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Permanently delete ${employee.full_name}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await deleteEmployee(employee.id);
      toast.success("Employee deleted.");
      // Trigger a back-nav via parent's onChanged? Actually caller handles removal on directory reload.
      // We'll set employee to null via a hard reload.
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
      setDeleting(false);
    }
  };

  const changeStatus = async (v) => {
    try {
      await updateEmployee(employee.id, { compliance_status: v });
      toast.success(`Status → ${STATUS_META[v]?.label}`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status update failed.");
    }
  };

  return (
    <header className="rounded-2xl border bg-white p-5 sm:p-6 flex flex-col md:flex-row gap-6"
            style={{ borderColor: "#E2E8F0" }}>
      {/* Photo */}
      <div className="shrink-0 relative">
        <EmployeePhoto employee={employee} size={128} rounded="rounded-2xl" refreshKey={photoKey} />
        <button
          type="button" onClick={() => photoRef.current?.click()}
          className="absolute -bottom-2 -right-2 h-9 w-9 rounded-full grid place-items-center border shadow"
          style={{ background: "#0B2545", color: "#FFD774", borderColor: "#0B2545" }}
          title="Upload photo"
          data-testid="employee-photo-upload"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
        </button>
        <input ref={photoRef} type="file" accept="image/jpeg,image/png,image/webp"
               className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        <div className="text-[10px] uppercase tracking-wider mt-3 text-center font-semibold" style={{ color: "#0B2545" }}>
          {employee.employee_id}
        </div>
      </div>

      {/* Identity block */}
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-3xl font-bold" style={{ color: "#0B2545" }}
              data-testid="employee-name">{employee.full_name}</h1>
          <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full"
                style={{ background: status.bg, color: status.color }}
                data-testid="employee-status-badge">
            {status.label}
          </span>
        </div>
        <div className="text-sm" style={{ color: "#64748B" }}>
          {employee.position || "—"}{employee.department ? ` · ${employee.department}` : ""}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm mt-3" style={{ color: "#475569" }}>
          {employee.email && (
            <div className="flex items-center gap-2 truncate"><Mail className="h-3.5 w-3.5" />{employee.email}</div>
          )}
          {employee.phone && (
            <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" />{employee.phone}</div>
          )}
          {employee.service_area && (
            <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" />{employee.service_area}</div>
          )}
          {employee.supervisor && (
            <div className="flex items-center gap-2"><User className="h-3.5 w-3.5" />Supervisor: {employee.supervisor}</div>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div className="shrink-0 flex flex-col gap-2 min-w-[220px]">
        <div className="text-[10px] uppercase tracking-[0.18em] font-medium" style={{ color: "#64748B" }}>
          Compliance status
        </div>
        <Select value={employee.compliance_status} onValueChange={changeStatus}>
          <SelectTrigger className="h-10" data-testid="employee-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {EMPLOYEE_COMPLIANCE_STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="ghost" onClick={remove} disabled={deleting}
                className="justify-start h-9 mt-4" style={{ color: "#9B1C1C" }}
                data-testid="employee-delete">
          {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
          Delete employee
        </Button>
      </div>
    </header>
  );
};

// ---------------------------------------------------------------------------
// Overview — editable identity + compliance progress
// ---------------------------------------------------------------------------
const OverviewTab = ({ employee, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      full_name: employee.full_name || "",
      position: employee.position || "",
      department: employee.department || "",
      employment_type: employee.employment_type || "",
      employment_status: employee.employment_status || "active",
      hire_date: employee.hire_date || "",
      email: employee.email || "",
      phone: employee.phone || "",
      home_address: employee.home_address || "",
      emergency_contact_name: employee.emergency_contact_name || "",
      emergency_contact_phone: employee.emergency_contact_phone || "",
      service_area: employee.service_area || "",
      supervisor: employee.supervisor || "",
      pay_type: employee.pay_type || "",
      primary_lab_partner: employee.primary_lab_partner || "",
      notes: employee.notes || "",
    });
  }, [employee.id]);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await updateEmployee(employee.id, form);
      toast.success("Saved.");
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally { setSaving(false); }
  };

  const checklist = employee.onboarding_checklist || [];
  const required = checklist.filter((it) => it.required);
  const completed = required.filter((it) => it.completed).length;
  const pct = required.length ? Math.round((completed / required.length) * 100) : 0;
  const missing = required.filter((it) => !it.completed);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="employee-overview">
      {/* Compliance progress card */}
      <section className="rounded-2xl border bg-white p-5 lg:col-span-1" style={{ borderColor: "#E2E8F0" }}
               data-testid="compliance-progress-card">
        <div className="text-[10px] uppercase tracking-[0.18em] font-medium" style={{ color: "#64748B" }}>
          Compliance Progress
        </div>
        <div className="mt-3 flex items-end gap-3">
          <div className="font-display text-5xl font-bold" style={{ color: "#0B2545" }}
               data-testid="compliance-progress-percent">{pct}%</div>
          <div className="text-xs pb-2" style={{ color: "#64748B" }}>Overall completion</div>
        </div>
        <div className="mt-3 h-2.5 rounded-full overflow-hidden" style={{ background: "#F1F5F9" }}>
          <div className="h-full transition-all"
               style={{ width: `${pct}%`,
                        background: pct === 100 ? "#0F6D5D" : "#D4AF37" }} />
        </div>
        <div className="text-xs mt-2" style={{ color: "#475569" }}>
          {completed} of {required.length} required items complete
        </div>

        {missing.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-[0.18em] font-medium mb-1.5" style={{ color: "#64748B" }}>
              Missing requirements
            </div>
            <ul className="space-y-1" data-testid="compliance-missing-list">
              {missing.slice(0, 8).map((it) => (
                <li key={it.key} className="text-xs flex items-center gap-1.5" style={{ color: "#B45309" }}>
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#B45309" }} />
                  {it.label}
                </li>
              ))}
              {missing.length > 8 && (
                <li className="text-xs" style={{ color: "#64748B" }}>+{missing.length - 8} more</li>
              )}
            </ul>
          </div>
        )}
      </section>

      {/* Identity / details card */}
      <section className="rounded-2xl border bg-white p-5 lg:col-span-2" style={{ borderColor: "#E2E8F0" }}
               data-testid="employee-details-card">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-lg font-semibold" style={{ color: "#0B2545" }}>
            Employee Summary
          </div>
          {editing ? (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditing(false)} disabled={saving}
                      className="h-9" data-testid="employee-edit-cancel">
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button onClick={save} disabled={saving} className="h-9"
                      style={{ background: "#0B2545", color: "#FFD774" }}
                      data-testid="employee-edit-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save</>}
              </Button>
            </div>
          ) : (
            <Button variant="outline" onClick={() => setEditing(true)} className="h-9"
                    style={{ borderColor: "#E2E8F0", color: "#0B2545" }}
                    data-testid="employee-edit-open">
              <Edit3 className="h-4 w-4 mr-1" /> Edit
            </Button>
          )}
        </div>

        {editing ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <EditField label="Full name">
              <Input value={form.full_name} onChange={set("full_name")} data-testid="edit-full-name" />
            </EditField>
            <EditField label="Position">
              <Input value={form.position} onChange={set("position")} data-testid="edit-position" />
            </EditField>
            <EditField label="Department">
              <Input value={form.department} onChange={set("department")} data-testid="edit-department" />
            </EditField>
            <EditField label="Employment type">
              <Input value={form.employment_type} onChange={set("employment_type")} data-testid="edit-emp-type" />
            </EditField>
            <EditField label="Hire date">
              <Input type="date" value={form.hire_date} onChange={set("hire_date")} data-testid="edit-hire-date" />
            </EditField>
            <EditField label="Supervisor">
              <Input value={form.supervisor} onChange={set("supervisor")} data-testid="edit-supervisor" />
            </EditField>
            <EditField label="Email"><Input value={form.email} onChange={set("email")} data-testid="edit-email" /></EditField>
            <EditField label="Phone"><Input value={form.phone} onChange={set("phone")} data-testid="edit-phone" /></EditField>
            <EditField label="Emergency contact name">
              <Input value={form.emergency_contact_name} onChange={set("emergency_contact_name")} />
            </EditField>
            <EditField label="Emergency contact phone">
              <Input value={form.emergency_contact_phone} onChange={set("emergency_contact_phone")} />
            </EditField>
            <EditField label="Service area">
              <Input value={form.service_area} onChange={set("service_area")} />
            </EditField>
            <EditField label="Pay type"><Input value={form.pay_type} onChange={set("pay_type")} /></EditField>
            <div className="sm:col-span-2">
              <EditField label="Home address"><Input value={form.home_address} onChange={set("home_address")} /></EditField>
            </div>
            <div className="sm:col-span-2">
              <EditField label="Primary lab partner">
                <Input value={form.primary_lab_partner} onChange={set("primary_lab_partner")} />
              </EditField>
            </div>
            <div className="sm:col-span-2">
              <EditField label="Notes">
                <Textarea rows={3} value={form.notes} onChange={set("notes")} />
              </EditField>
            </div>
          </div>
        ) : (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <Info label="Employee ID"           value={employee.employee_id} />
            <Info label="Position"              value={employee.position} />
            <Info label="Department"            value={employee.department} />
            <Info label="Employment Type"       value={employee.employment_type} />
            <Info label="Employment Status"     value={employee.employment_status} />
            <Info label="Hire Date"             value={_fmtDate(employee.hire_date)} />
            <Info label="Email"                 value={employee.email} />
            <Info label="Phone"                 value={employee.phone} />
            <Info label="Supervisor"            value={employee.supervisor} />
            <Info label="Service Area"          value={employee.service_area} />
            <Info label="Pay Type"              value={employee.pay_type} />
            <Info label="Primary Lab Partner"   value={employee.primary_lab_partner} />
            <Info label="Emergency Contact"     value={
              employee.emergency_contact_name
                ? `${employee.emergency_contact_name}${employee.emergency_contact_phone ? ` (${employee.emergency_contact_phone})` : ""}`
                : "—"
            } />
            <Info label="Home Address"          value={employee.home_address} />
            {employee.notes && (
              <div className="sm:col-span-2 mt-2 rounded-xl p-3" style={{ background: "#F8FAFC", color: "#475569" }}>
                {employee.notes}
              </div>
            )}
          </dl>
        )}
      </section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Credentials — grouped by category, credential-card style
// ---------------------------------------------------------------------------
const CredentialsTab = ({ employee }) => {
  const cards = employee.credentials || [];
  const grouped = useMemo(() => {
    const g = {};
    cards.forEach((c) => {
      const cat = c.category || "miscellaneous";
      (g[cat] = g[cat] || []).push(c);
    });
    return g;
  }, [cards]);

  if (!cards.length) {
    return (
      <div className="rounded-2xl border-2 border-dashed py-16 text-center"
           style={{ borderColor: "#E2E8F0", color: "#64748B" }}
           data-testid="credentials-empty">
        <FileText className="h-8 w-8 mx-auto mb-3" style={{ color: "#94A3B8" }} />
        <p className="font-medium" style={{ color: "#0B2545" }}>No credentials on file yet.</p>
        <p className="text-sm mt-1 max-w-md mx-auto">
          Upload records under Operations &amp; Docs with this employee&apos;s name
          to populate credential cards automatically.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="credentials-tab">
      {EMPLOYEE_CREDENTIAL_CATEGORIES.map((cat) => {
        const list = grouped[cat.value];
        if (!list?.length) return null;
        return (
          <section key={cat.value}>
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-2"
                 style={{ color: "#7A5E12" }}>{cat.label}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {list.map((c) => <CredentialCard key={c.key} card={c} />)}
            </div>
          </section>
        );
      })}
    </div>
  );
};

const CredentialCard = ({ card }) => {
  const statusMeta = {
    verified:      { label: "Verified",      bg: "rgba(15,109,93,0.10)",  fg: "#0F6D5D", Icon: Check },
    expiring_soon: { label: "Expiring Soon", bg: "rgba(212,175,55,0.18)", fg: "#7A5E12", Icon: Clock3 },
    expired:       { label: "Expired",       bg: "rgba(155,28,28,0.10)",  fg: "#9B1C1C", Icon: AlertTriangle },
    needs_review:  { label: "Needs Review",  bg: "rgba(180,83,9,0.12)",   fg: "#B45309", Icon: Clock3 },
    rejected:      { label: "Rejected",      bg: "rgba(155,28,28,0.10)",  fg: "#9B1C1C", Icon: X },
    pending:       { label: "Pending Upload",bg: "#F1F5F9",               fg: "#475569", Icon: UploadIcon },
  }[card.status] || { label: card.status || "Pending", bg: "#F1F5F9", fg: "#475569", Icon: Clock3 };

  const StatusIcon = statusMeta.Icon;
  const doc = card.documents?.[0];

  return (
    <div className="rounded-2xl border bg-white p-4" style={{ borderColor: "#E2E8F0" }}
         data-testid={`credential-card-${card.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display font-semibold truncate" style={{ color: "#0B2545" }}>
            {card.label}
          </div>
          <div className="text-[10px] uppercase tracking-wider mt-0.5" style={{ color: "#94A3B8" }}>
            {CATEGORY_LABEL[card.category] || card.category}
          </div>
        </div>
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full whitespace-nowrap"
              style={{ background: statusMeta.bg, color: statusMeta.fg }}
              data-testid={`credential-status-${card.key}`}>
          <StatusIcon className="h-3 w-3" /> {statusMeta.label}
        </span>
      </div>

      <dl className="mt-3 grid gap-1 text-xs" style={{ color: "#475569" }}>
        {card.earliest_expiration && (
          <div className="flex items-center gap-1.5">
            <CalendarClock className="h-3 w-3" />
            <span>Expires: {_fmtDate(card.earliest_expiration)}</span>
            {_daysHint(card.earliest_expiration)}
          </div>
        )}
        {doc?.uploaded_at && (
          <div className="flex items-center gap-1.5">
            <Calendar className="h-3 w-3" />
            <span>Uploaded: {_fmtDate(doc.uploaded_at)}</span>
          </div>
        )}
        {card.documents.length > 1 && (
          <div className="text-[10px]" style={{ color: "#94A3B8" }}>
            +{card.documents.length - 1} additional file{card.documents.length > 2 ? "s" : ""}
          </div>
        )}
      </dl>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Onboarding — editable checklist
// ---------------------------------------------------------------------------
const OnboardingTab = ({ employee, onChanged }) => {
  const [busyKey, setBusyKey] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newRequired, setNewRequired] = useState(true);

  const checklist = employee.onboarding_checklist || [];
  const required = checklist.filter((it) => it.required);
  const completed = required.filter((it) => it.completed).length;
  const pct = required.length ? Math.round((completed / required.length) * 100) : 0;

  const toggle = async (item) => {
    setBusyKey(item.key);
    try {
      await toggleOnboardingItem(employee.id, item.key, { completed: !item.completed });
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update.");
    } finally { setBusyKey(null); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Remove checklist item "${item.label}"?`)) return;
    setBusyKey(item.key);
    try {
      await removeOnboardingItem(employee.id, item.key);
      toast.success("Removed.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove.");
    } finally { setBusyKey(null); }
  };

  const add = async () => {
    if (!newLabel.trim()) return;
    setAdding(true);
    try {
      await addOnboardingItem(employee.id, { label: newLabel.trim(), required: newRequired });
      setNewLabel("");
      setNewRequired(true);
      toast.success("Added.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add.");
    } finally { setAdding(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6" data-testid="onboarding-tab">
      <div>
        <div className="rounded-2xl border bg-white overflow-hidden" style={{ borderColor: "#E2E8F0" }}>
          <div className="px-5 py-3 border-b flex items-center justify-between"
               style={{ borderColor: "#E2E8F0", background: "#F8FAFC" }}>
            <div className="text-sm font-semibold" style={{ color: "#0B2545" }}>Onboarding Checklist</div>
            <div className="text-xs" style={{ color: "#64748B" }}>
              {completed}/{required.length} required · {pct}%
            </div>
          </div>
          <ul className="divide-y" style={{ borderColor: "#E2E8F0" }} data-testid="onboarding-checklist">
            {checklist.map((it) => (
              <li key={it.key} className="px-5 py-3 flex items-center gap-3"
                  data-testid={`checklist-item-${it.key}`}>
                <button
                  type="button"
                  onClick={() => toggle(it)}
                  disabled={busyKey === it.key}
                  className="h-5 w-5 rounded border grid place-items-center shrink-0"
                  style={{
                    background: it.completed ? "#0F6D5D" : "#FFFFFF",
                    borderColor: it.completed ? "#0F6D5D" : "#CBD5E1",
                  }}
                  data-testid={`checklist-toggle-${it.key}`}
                >
                  {busyKey === it.key ? (
                    <Loader2 className="h-3 w-3 animate-spin" style={{ color: it.completed ? "#FFF" : "#94A3B8" }} />
                  ) : it.completed ? (
                    <Check className="h-3 w-3" style={{ color: "#FFF" }} />
                  ) : null}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate"
                       style={{
                         color: it.completed ? "#94A3B8" : "#0B2545",
                         textDecoration: it.completed ? "line-through" : "none",
                       }}>
                    {it.label}
                    {!it.required && (
                      <span className="ml-2 text-[9px] uppercase tracking-wider" style={{ color: "#94A3B8" }}>Optional</span>
                    )}
                    {it.is_custom && (
                      <span className="ml-2 text-[9px] uppercase tracking-wider" style={{ color: "#7A5E12" }}>Custom</span>
                    )}
                  </div>
                  {it.completed_at && (
                    <div className="text-[10px]" style={{ color: "#94A3B8" }}>
                      Completed {_fmtDate(it.completed_at)}{it.completed_by ? ` · ${it.completed_by}` : ""}
                    </div>
                  )}
                </div>
                {it.is_custom && (
                  <button type="button" onClick={() => remove(it)}
                          className="text-[11px] px-2 py-1 rounded hover:bg-slate-100"
                          style={{ color: "#9B1C1C" }}
                          data-testid={`checklist-remove-${it.key}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Add custom item */}
      <aside className="rounded-2xl border bg-white p-5 h-fit" style={{ borderColor: "#E2E8F0" }}>
        <div className="font-display font-semibold mb-3" style={{ color: "#0B2545" }}>
          Add checklist item
        </div>
        <div className="text-xs mb-3" style={{ color: "#64748B" }}>
          Add employee-specific requirements. Custom items can be removed later.
        </div>
        <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
               placeholder="e.g. Blood pressure certification"
               className="h-10" data-testid="onboarding-add-label" />
        <label className="mt-3 flex items-center gap-2 text-xs cursor-pointer" style={{ color: "#475569" }}>
          <input type="checkbox" checked={newRequired}
                 onChange={(e) => setNewRequired(e.target.checked)}
                 data-testid="onboarding-add-required" />
          Required for Ready-for-Dispatch
        </label>
        <Button onClick={add} disabled={adding || !newLabel.trim()}
                className="mt-3 w-full h-10"
                style={{ background: "#0B2545", color: "#FFD774" }}
                data-testid="onboarding-add-submit">
          {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Plus className="h-4 w-4 mr-1" /> Add item</>)}
        </Button>
        {pct === 100 && (
          <div className="mt-4 rounded-xl p-3 text-xs flex items-center gap-2"
               style={{ background: "rgba(15,109,93,0.10)", color: "#0F6D5D" }}>
            <ShieldCheck className="h-4 w-4" /> All required items complete — this employee is Ready for Dispatch.
          </div>
        )}
      </aside>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Activity — timeline
// ---------------------------------------------------------------------------
const ActivityTab = ({ employee }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const t = await getEmployeeTimeline(employee.id);
        if (!cancelled) setEntries(t.timeline || []);
      } catch { /* handled by parent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [employee.id]);

  if (loading) {
    return <div className="grid place-items-center py-12"><Loader2 className="h-5 w-5 animate-spin" style={{ color: "#0B2545" }} /></div>;
  }
  if (!entries.length) {
    return (
      <div className="rounded-2xl border-2 border-dashed py-16 text-center"
           style={{ borderColor: "#E2E8F0", color: "#64748B" }}
           data-testid="activity-empty">
        <Activity className="h-8 w-8 mx-auto mb-3" style={{ color: "#94A3B8" }} />
        No activity yet.
      </div>
    );
  }
  return (
    <ol className="relative border-l ml-3 space-y-4" style={{ borderColor: "#E2E8F0" }}
        data-testid="activity-timeline">
      {entries.map((e, i) => (
        <li key={i} className="ml-4">
          <span className="absolute -left-1.5 h-3 w-3 rounded-full"
                style={{ background: _kindColor(e.kind), border: "2px solid #FFFFFF" }} />
          <div className="text-xs" style={{ color: "#94A3B8" }}>{_fmtDT(e.ts)}</div>
          <div className="text-sm mt-0.5" style={{ color: "#0B2545" }}>{e.message}</div>
          {e.actor && <div className="text-[10px] mt-0.5" style={{ color: "#7A5E12" }}>by {e.actor}</div>}
        </li>
      ))}
    </ol>
  );
};

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
const _fmtDate = (v) => { if (!v) return "—"; try { return format(parseISO(v), "PP"); } catch { return v; } };
const _fmtDT = (v) => { if (!v) return "—"; try { return format(parseISO(v), "PPp"); } catch { return v; } };
const _daysHint = (v) => {
  try {
    const days = differenceInCalendarDays(parseISO(v), new Date());
    if (days < 0) return <span className="ml-1 text-[10px]" style={{ color: "#9B1C1C" }}>({-days}d overdue)</span>;
    if (days <= 60) return <span className="ml-1 text-[10px]" style={{ color: "#B45309" }}>({days}d)</span>;
    return null;
  } catch { return null; }
};
const _kindColor = (k) => ({
  created: "#0F6D5D",
  status_change: "#1D4ED8",
  checklist_toggle: "#7A5E12",
  doc_uploaded: "#0B2545",
  photo: "#B45309",
}[k] || "#94A3B8");

const EditField = ({ label, children }) => (
  <div>
    <div className="text-[11px] uppercase tracking-[0.18em] font-medium mb-1" style={{ color: "#0B2545" }}>{label}</div>
    {children}
  </div>
);
const Info = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.18em] font-medium" style={{ color: "#94A3B8" }}>{label}</div>
    <div className="text-sm mt-0.5" style={{ color: value ? "#0B2545" : "#94A3B8" }}>{value || "—"}</div>
  </div>
);
